import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, CreditCard, Moon, Sun, LogOut, ChevronDown } from 'lucide-react';
import Dashboard from '../components/Dashboard'; 
import GenPro from '../components/GenPro';
import Storyteller from '../components/Storyteller';
import { useTheme } from '../App';
import '../App.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Platform definitions
const PLATFORMS = {
  youtube: { name: 'YouTube', icon: '▶️', color: '#FF0000' },
  instagram: { name: 'Instagram', icon: '📷', color: '#E4405F' },
  tiktok: { name: 'TikTok', icon: '🎵', color: '#000000' },
  facebook: { name: 'Facebook', icon: '📘', color: '#1877F2' },
};

// Plan limits (should match backend)
const PLAN_LIMITS = {
  free: { channels: 1, price: 0 },
  starter: { channels: 3, price: 3 },
  creator: { channels: 5, price: 7 },
  business: { channels: 10, price: 15 },
};

// --- HELPER: Safely parse tools ---
const getTools = (channel) => {
  if (!channel || !channel.enabled_tools) return [];
  if (Array.isArray(channel.enabled_tools)) return channel.enabled_tools;
  try {
    return JSON.parse(channel.enabled_tools);
  } catch (e) {
    console.warn("Could not parse enabled_tools:", channel.enabled_tools);
    return [];
  }
};

// --- MODAL: UPGRADE PLAN ---
function UpgradeModal({ onClose, currentPlan, channelsUsed }) {
  const navigate = useNavigate();
  
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>⬆️ Upgrade Your Plan</h2>
        <p style={{color: '#888', marginBottom: '20px'}}>
          You've reached your channel limit ({channelsUsed}/{PLAN_LIMITS[currentPlan]?.channels || 1}).
          Upgrade to connect more channels.
        </p>
        
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px'}}>
          {Object.entries(PLAN_LIMITS).map(([plan, data]) => (
            <div 
              key={plan}
              style={{
                padding: '15px',
                background: plan === currentPlan ? '#333' : '#222',
                borderRadius: '8px',
                border: plan === currentPlan ? '2px solid #FF4B1F' : '1px solid #444',
                cursor: plan === currentPlan ? 'default' : 'pointer'
              }}
            >
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                  <strong style={{textTransform: 'capitalize'}}>{plan}</strong>
                  <span style={{color: '#888', marginLeft: '10px'}}>{data.channels} channel{data.channels > 1 ? 's' : ''}</span>
                </div>
                <div style={{color: '#FF9068', fontWeight: 'bold'}}>
                  {data.price === 0 ? 'Free' : `$${data.price}/mo`}
                </div>
              </div>
              {plan === currentPlan && <span style={{fontSize: '0.8rem', color: '#FF4B1F'}}>Current Plan</span>}
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn-text">Cancel</button>
          <button onClick={() => navigate('/pricing')} className="btn-primary">View Pricing</button>
        </div>
      </div>
    </div>
  );
}

// Helper function to get user's enabled tools from localStorage
const getUserEnabledTools = () => {
  try {
    const stored = localStorage.getItem('vlumina_user');
    if (stored) {
      const user = JSON.parse(stored);
      return JSON.parse(user.enabled_tools || '[]');
    }
  } catch {}
  return [];
};

// --- MODAL: PLATFORM SELECTOR ---
function PlatformSelectorModal({ onClose, onSelect }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '400px'}}>
        <h2>🔗 Connect a Channel</h2>
        <p style={{color: '#888', marginBottom: '20px'}}>
          Choose a platform to connect your channel.
        </p>
        
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
          {Object.entries(PLATFORMS).map(([key, platform]) => (
            <button
              key={key}
              onClick={() => onSelect(key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '15px 20px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                cursor: key === 'youtube' ? 'pointer' : 'not-allowed',
                opacity: key === 'youtube' ? 1 : 0.5,
                transition: 'all 0.2s',
                color: 'var(--text-main)',
                fontSize: '1rem'
              }}
              disabled={key !== 'youtube'}
            >
              <span style={{fontSize: '1.5rem'}}>{platform.icon}</span>
              <span style={{fontWeight: '600'}}>{platform.name}</span>
              {key !== 'youtube' && (
                <span style={{marginLeft: 'auto', fontSize: '0.75rem', color: '#888'}}>Coming Soon</span>
              )}
            </button>
          ))}
        </div>

        <div className="modal-actions" style={{marginTop: '20px'}}>
          <button onClick={onClose} className="btn-text">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// --- USER PROFILE DROPDOWN ---
function UserProfileDropdown({ user, onLogout, theme, onThemeChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  return (
    <div style={{position: 'relative'}}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: '50px',
          padding: '8px 12px',
          cursor: 'pointer',
          color: 'var(--text-main)'
        }}
      >
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #FF4B1F, #FF9068)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <ChevronDown size={16} />
      </button>
      
      {isOpen && (
        <>
          <div 
            style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99}}
            onClick={() => setIsOpen(false)}
          />
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            padding: '8px',
            minWidth: '200px',
            zIndex: 100,
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{padding: '12px', borderBottom: '1px solid var(--border-color)', marginBottom: '8px'}}>
              <div style={{fontWeight: 'bold', color: 'var(--text-main)'}}>{user?.name || 'User'}</div>
              <div style={{fontSize: '0.8rem', color: 'var(--text-muted)'}}>{user?.email}</div>
              <div style={{fontSize: '0.75rem', color: '#FF9068', marginTop: '4px', textTransform: 'capitalize'}}>
                {user?.plan || 'free'} Plan
              </div>
            </div>
            
            <button 
              onClick={() => { setIsOpen(false); navigate('/settings'); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px 12px',
                background: 'none',
                border: 'none',
                color: 'var(--text-main)',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left'
              }}
              onMouseOver={e => e.target.style.background = 'var(--bg-secondary)'}
              onMouseOut={e => e.target.style.background = 'none'}
            >
              <Settings size={16} /> Account Settings
            </button>
            
            <button 
              onClick={() => { setIsOpen(false); navigate('/pricing'); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px 12px',
                background: 'none',
                border: 'none',
                color: 'var(--text-main)',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left'
              }}
              onMouseOver={e => e.target.style.background = 'var(--bg-secondary)'}
              onMouseOut={e => e.target.style.background = 'none'}
            >
              <CreditCard size={16} /> Billing & Plans
            </button>
            
            <button 
              onClick={() => { onThemeChange(theme === 'dark' ? 'light' : 'dark'); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '10px 12px',
                background: 'none',
                border: 'none',
                color: 'var(--text-main)',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left'
              }}
              onMouseOver={e => e.target.style.background = 'var(--bg-secondary)'}
              onMouseOut={e => e.target.style.background = 'none'}
            >
              {theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
              {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
            </button>
            
            <div style={{borderTop: '1px solid var(--border-color)', marginTop: '8px', paddingTop: '8px'}}>
              <button 
                onClick={() => { setIsOpen(false); onLogout(); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '10px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#ff6b6b',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  textAlign: 'left'
                }}
                onMouseOver={e => e.target.style.background = '#331a1a'}
                onMouseOut={e => e.target.style.background = 'none'}
              >
                <LogOut size={16} /> Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// --- MODAL: SETTINGS (Reorderable) ---
function SettingsModal({ channel, onClose, onUpdate }) {
  const allToolsDefinition = [
    { id: 'gen_pro', label: '🎬 YouTube Playlist' },
    { id: 'storyteller', label: '📖 Storyteller' }
  ];
  
  // Only show tools the user has purchased
  const userEnabledTools = getUserEnabledTools();

  const [toolList, setToolList] = useState(() => {
    const saved = getTools(channel);
    
    // Filter to only tools user has access to (purchased)
    const availableTools = allToolsDefinition.filter(def => userEnabledTools.includes(def.id));
    
    // Filter OUT dashboard from draggable list
    const draggable = saved.filter(t => t !== 'dashboard' && userEnabledTools.includes(t)).map(id => {
      const def = availableTools.find(t => t.id === id);
      return def ? { ...def, enabled: true } : null;
    }).filter(Boolean);

    availableTools.forEach(def => {
      if (!saved.includes(def.id) && def.id !== 'dashboard') {
        draggable.push({ ...def, enabled: false });
      }
    });
    return draggable;
  });

  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  const toggleTool = (index) => {
    const newList = [...toolList];
    newList[index].enabled = !newList[index].enabled;
    setToolList(newList);
  };

  const onDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e, index) => {
    e.preventDefault(); 
    if (draggedItemIndex === null || draggedItemIndex === index) return;
    const newList = [...toolList];
    const draggedItem = newList[draggedItemIndex];
    newList.splice(draggedItemIndex, 1);
    newList.splice(index, 0, draggedItem);
    setDraggedItemIndex(index);
    setToolList(newList);
  };

  const handleSave = async () => {
    const finalOrderedList = ['dashboard', ...toolList.filter(t => t.enabled).map(t => t.id)];
    try {
      await fetch(`${API_URL}/channels/${channel.id}/tools`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tools: finalOrderedList })
      });
      onUpdate(); 
      onClose();
    } catch (err) { alert("Failed to update settings"); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>⚙️ Configure Tools</h2>
        <p style={{color: '#888', marginBottom: '20px'}}>Reorder or toggle tools.</p>

        <div className="tools-checklist">
          {/* STATIC DASHBOARD */}
          <div className="draggable-item" style={{opacity: 0.7, borderStyle: 'dashed', cursor: 'default'}}>
            <span className="drag-handle" style={{color: '#444'}}>🔒</span>
            <input type="checkbox" checked disabled style={{marginRight: '12px', accentColor: '#555'}}/>
            <span>Channel Dashboard</span>
          </div>

          {/* DRAGGABLE ITEMS */}
          {toolList.map((tool, index) => (
            <div 
              key={tool.id}
              className={`draggable-item ${draggedItemIndex === index ? 'dragging' : ''}`}
              draggable
              onDragStart={(e) => onDragStart(e, index)}
              onDragOver={(e) => onDragOver(e, index)}
              onDragEnd={() => setDraggedItemIndex(null)}
            >
              <span className="drag-handle">⋮⋮</span>
              <input type="checkbox" checked={tool.enabled} onChange={() => toggleTool(index)} style={{marginRight: '12px'}}/>
              <span style={{opacity: tool.enabled ? 1 : 0.5}}>{tool.label}</span>
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn-text">Cancel</button>
          <button onClick={handleSave} className="btn-primary">Save Changes</button>
        </div>
      </div>
    </div>
  );
}

// --- MAIN DASHBOARD APP ---
function DashboardApp() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPlatformSelector, setShowPlatformSelector] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  
  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(() => {
    const stored = localStorage.getItem('vlumina_user');
    return stored ? JSON.parse(stored) : null;
  });

  // Fetch fresh user data from backend
  const refreshUserData = async () => {
    const token = localStorage.getItem('vlumina_token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const userData = await res.json();
        localStorage.setItem('vlumina_user', JSON.stringify(userData));
        setCurrentUser(userData);
      }
    } catch (err) {
      console.error('Failed to refresh user data:', err);
    }
  };

  useEffect(() => { 
    fetchChannels(); 
    refreshUserData(); // Refresh user data on mount
  }, []);
  
  // Check if user can add more channels
  const userPlan = currentUser?.plan || 'free';
  const channelLimit = PLAN_LIMITS[userPlan]?.channels || 1;
  const canAddChannel = channels.length < channelLimit;
  
  // Get user's enabled tools (from current state, not just localStorage)
  const userEnabledTools = (() => {
    try {
      return JSON.parse(currentUser?.enabled_tools || '[]');
    } catch {
      return [];
    }
  })();
  
  const handleConnectClick = () => {
    if (canAddChannel) {
      setShowPlatformSelector(true);
    } else {
      setShowUpgradeModal(true);
    }
  };

  const handlePlatformSelect = (platform) => {
    setShowPlatformSelector(false);
    if (platform === 'youtube') {
      const toolsToEnable = ['dashboard', ...userEnabledTools].join(',');
      window.location.href = `${API_URL}/auth/login?tools=${toolsToEnable}&user_id=${currentUser?.id || ''}`;
    }
    // Other platforms will be added later
  };
  
  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
  };

  const fetchChannels = async () => {
    try {
      const userId = currentUser?.id || '';
      const res = await fetch(`${API_URL}/channels?user_id=${userId}`);
      const data = await res.json();
      setChannels(data.channels);
      
      if (data.channels.length > 0) {
          if (activeChannel) {
              const updated = data.channels.find(c => c.id === activeChannel.id);
              if (updated) setActiveChannel(updated);
          } else {
              setActiveChannel(data.channels[0]);
              const tools = getTools(data.channels[0]);
              if (tools.length > 0) setActiveTool(tools[0]);
          }
      } else {
          setActiveChannel(null);
      }
    } catch (err) { console.error(err); }
  };

  const deleteChannel = async (id, e) => {
    e.stopPropagation();
    if(!window.confirm("Disconnect this channel?")) return;
    await fetch(`${API_URL}/channels/${id}`, { method: 'DELETE' });
    setActiveChannel(null);
    fetchChannels();
  };

  const handleLogout = () => {
    localStorage.removeItem('vlumina_user');
    localStorage.removeItem('vlumina_token');
    navigate('/');
  };

  const renderTool = () => {
    if (!activeChannel) return (
      <div className="placeholder-container">
        <h2>👋 Welcome to V Lumina AI</h2>
        <p>Connect a YouTube channel to start managing.</p>
      </div>
    );
    
    if (!activeTool) return <div className="placeholder-container">Select a tool.</div>;
    
    switch (activeTool) {
      case 'dashboard': return <Dashboard channel={activeChannel} />;
      case 'gen_pro': return <GenPro />;
      case 'storyteller': return <Storyteller />;
      default: return <div>Unknown Tool</div>;
    }
  };

  // Tool tabs: Dashboard is always shown, then intersection of user's purchased tools AND channel's enabled tools
  const channelTools = getTools(activeChannel);
  const activeTools = ['dashboard', ...userEnabledTools.filter(tool => 
    channelTools.includes(tool) || channelTools.length === 0
  )];

  return (
    <div className="app-container" data-theme={theme}>
      <div className="sidebar">
        <div className="brand"><span>✨ V Lumina AI</span></div>
        <div className="section-label">YOUR CHANNELS</div>
        <div className="channel-list">
          {channels.map(ch => {
            const platform = PLATFORMS[ch.platform || 'youtube'];
            return (
              <div 
                key={ch.id} 
                className={`channel-item ${activeChannel?.id === ch.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveChannel(ch);
                  const tools = getTools(ch);
                  if (tools.length > 0) setActiveTool(tools[0]);
                }}
              >
                <img src={ch.thumbnail_url} alt="" className="channel-thumb"/>
                <div className="channel-info">
                  <div className="channel-name">{ch.name}</div>
                  <div className="channel-handle">{ch.handle}</div>
                </div>
                <span className="channel-platform-logo">{platform?.icon}</span>
                <div className="channel-actions">
                  <button className="btn-icon" onClick={(e) => {e.stopPropagation(); setEditingChannel(ch);}}>⚙️</button>
                  <button className="btn-icon delete" onClick={(e) => deleteChannel(ch.id, e)}>×</button>
                </div>
              </div>
            );
          })}
        </div>
        <button className="btn-add-channel" onClick={handleConnectClick}>
          <span style={{marginRight: '8px'}}>{canAddChannel ? '➕' : '⬆️'}</span> 
          {canAddChannel ? 'Connect Channel' : 'Upgrade to Add'}
        </button>
        
        <div style={{padding: '10px 15px', fontSize: '0.75rem', color: '#666'}}>
          {channels.length}/{channelLimit} channels used
        </div>
        
        <div className="sidebar-footer">
          <button className="btn-logout" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
        </div>
      </div>

      <div className="main-content">
        {/* User Profile in top right */}
        <div style={{position: 'absolute', top: '15px', right: '20px', zIndex: 50}}>
          <UserProfileDropdown 
            user={currentUser} 
            onLogout={handleLogout}
            theme={theme}
            onThemeChange={handleThemeChange}
          />
        </div>
        {activeChannel && (
          <div className={`workspace-header ${headerCollapsed ? 'collapsed' : ''}`}>
            <div className="workspace-title">
                <img src={activeChannel.thumbnail_url} alt="" style={{width: '40px', height: '40px', borderRadius: '50%'}} />
                <div>
                    <h1 style={{margin: 0, fontSize: '1.5rem'}}>{activeChannel.name}</h1>
                    <div style={{fontSize: '0.8rem', color: '#888'}}>{activeChannel.handle} • Connected</div>
                </div>
            </div>
            
            <div className="tool-tabs">
                {activeTools.map(tool => (
                    <button 
                        key={tool}
                        className={`tab-btn ${activeTool === tool ? 'active' : ''}`}
                        onClick={() => setActiveTool(tool)}
                    >
                        {tool === 'dashboard' && '📊 Dashboard'}
                        {tool === 'lyrics' && '✨ AI Lyricist'}
                        {tool === 'music' && '🎵 Music Studio'}
                        {tool === 'gen_pro' && '🚀 Gen Pro'}
                        {tool === 'storyteller' && '📖 Storyteller'}
                        {tool === 'analysis' && '📉 Analysis'}
                        {tool === 'trainer' && '🧠 Gym'}
                    </button>
                ))}
            </div>
            
            <button 
                className="header-toggle"
                onClick={() => setHeaderCollapsed(!headerCollapsed)}
                title={headerCollapsed ? "Expand header" : "Collapse header"}
            >
                {headerCollapsed ? '▼' : '▲'}
            </button>
          </div>
        )}
        <div className="tool-viewport">{renderTool()}</div>
      </div>

      {showUpgradeModal && (
        <UpgradeModal 
          onClose={() => setShowUpgradeModal(false)} 
          currentPlan={userPlan}
          channelsUsed={channels.length}
        />
      )}
      
      {editingChannel && (
        <SettingsModal 
            channel={editingChannel} 
            onClose={() => setEditingChannel(null)} 
            onUpdate={fetchChannels} 
        />
      )}

      {showPlatformSelector && (
        <PlatformSelectorModal
          onClose={() => setShowPlatformSelector(false)}
          onSelect={handlePlatformSelect}
        />
      )}
    </div>
  );
}

export default DashboardApp;
